import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import Surface from '../components/Surface';
import { Heading, Subheading, Body, Label, Accent } from '../components/Typography';
import ScrollReveal, { StaggerContainer, StaggerItem } from '../components/ScrollReveal';

const PageWrapper = styled(Surface).attrs({ tone: 'base' })`
  min-height: 60vh;
`;

const Hero = styled.div`
  padding: 4rem 2rem 3rem;
  max-width: 1280px;
  margin: 0 auto;
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 2rem;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    padding: 2.5rem 1.5rem 2rem;
  }
`;

const HeroText = styled.div`
  flex: 1;
`;

const PageLabel = styled(Label)`
  display: block;
  color: #a0412d;
  margin-bottom: 0.75rem;
`;

const PageTitle = styled(Heading)`
  font-size: clamp(2rem, 5vw, 3rem);
  font-weight: 800;
`;

const HeroImage = styled.img`
  width: 200px;
  height: 240px;
  object-fit: cover;
  border-radius: 1.5rem 0.5rem 1.5rem 0.5rem;
  flex-shrink: 0;

  @media (max-width: 768px) {
    width: 140px;
    height: 168px;
  }
`;

// ─── Section: Genesis Energy Campaign ────────────────────────────────────────

const CampaignSection = styled(Surface).attrs({ tone: 'inset' })`
  padding: 3rem 2rem;
`;

const SectionInner = styled.div`
  max-width: 1280px;
  margin: 0 auto;
`;

const SectionHead = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  margin-bottom: 1.5rem;
  flex-wrap: wrap;
  gap: 0.5rem;
`;


const VideoCard = styled(Surface).attrs({ tone: 'raised' })`
  border-radius: 1rem;
  overflow: hidden;
  box-shadow: 0 20px 40px rgba(56, 49, 45, 0.06);
`;

const VideoEmbed = styled.div`
  position: relative;
  padding-bottom: 56.25%;
  height: 0;
`;

const ThumbnailLink = styled.a`
  display: block;
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-size: cover;
  background-position: center;
  background-color: #1a1a1a;
  text-decoration: none;
  
  &::after {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 60px;
    height: 60px;
    background-color: ${props => props.$type === 'facebook' ? 'rgba(24, 119, 242, 0.85)' : 'rgba(255, 0, 0, 0.85)'};
    border-radius: 50%;
    transition: transform 0.2s ease, background-color 0.2s ease;
  }

  &::before {
    content: '';
    position: absolute;
    top: 50%;
    left: 52%;
    transform: translate(-50%, -50%);
    border-style: solid;
    border-width: 10px 0 10px 15px;
    border-color: transparent transparent transparent white;
    z-index: 1;
    transition: transform 0.2s ease;
  }

  &:hover::after {
    background-color: ${props => props.$type === 'facebook' ? '#1877f2' : '#ff0000'};
    transform: translate(-50%, -50%) scale(1.1);
  }
  
  &:hover::before {
    transform: translate(-50%, -50%) scale(1.1);
  }
`;

const VideoCaption = styled.div`
  padding: 1rem 1.25rem;
`;

const VideoTitle = styled.p`
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-size: 0.9375rem;
  font-weight: 600;
  color: #38312d;
  margin-bottom: 0.25rem;
`;

const VideoMeta = styled(Label)`
  color: #665d58;
`;

// ─── Component ────────────────────────────────────────────────────────────────

const Portfolio = () => (
  <PageWrapper>
    <Hero>
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
        style={{ flex: 1 }}
      >
        <PageLabel>Selected Work</PageLabel>
        <PageTitle>Curated <Accent>Works.</Accent></PageTitle>
        <Body style={{ marginTop: '0.75rem', maxWidth: 440 }}>
          A showcase of commercial, stage, and media work — from national television
          campaigns to theatrical productions.
        </Body>
      </motion.div>
      <motion.div
        initial={{ opacity: 0, scale: 0.94 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.7, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
      >
        <HeroImage src="/sari.png" alt="Sari Angus" />
      </motion.div>
    </Hero>

    <CampaignSection>
      <SectionInner>
        <ScrollReveal variant="up">
          <SectionHead>
            <div>
              <Label style={{ color: '#a0412d', display: 'block', marginBottom: '0.4rem' }}>
                Television Commercial
              </Label>
              <Subheading>Genesis Energy Campaign</Subheading>
            </div>
            <Body style={{ fontSize: '0.875rem' }}>2022 – 2024</Body>
          </SectionHead>
        </ScrollReveal>
        <StaggerContainer
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
            gap: '1.5rem',
          }}
          stagger={0.09}
        >
          {[
            { type: 'youtube', src: 'yOC0FMl0OIs', title: 'Introducing George and her family', year: '2022' },
            { type: 'youtube', src: 'Mu6Hu0KI8n4', title: 'George and her family hit the road', year: '2022' },
            { type: 'facebook', src: '6350053441685112', title: 'George explains renewable energy', year: '2023' },
            { type: 'youtube', src: 'YS5HWT26mCk', title: 'George becomes a world-class inventor', year: '2023' },
            { type: 'youtube', src: 'lpV0RcH8E4U', title: 'George times a Power Shout', year: '2024' },
            { type: 'youtube', src: 'XGRvLCWJfGQ', title: 'George helps create a warm home', year: '2024' },
          ].map(({ type, src, title, year }) => (
            <StaggerItem key={src}>
              <VideoCard>
                <VideoEmbed>
                  <ThumbnailLink 
                    href={type === 'facebook' ? `https://www.facebook.com/watch/?v=${src}` : `https://www.youtube.com/watch?v=${src}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    $type={type}
                    style={{ backgroundImage: type === 'youtube' ? `url(https://img.youtube.com/vi/${src}/hqdefault.jpg)` : 'none' }}
                  >
                    {type === 'facebook' && (
                      <span style={{ 
                        position: 'absolute', 
                        width: '100%', 
                        bottom: '15%', 
                        textAlign: 'center', 
                        color: 'white', 
                        fontWeight: '600', 
                        fontFamily: "'Plus Jakarta Sans', sans-serif" 
                      }}>
                        Watch on Facebook
                      </span>
                    )}
                  </ThumbnailLink>
                </VideoEmbed>
                <VideoCaption>
                  <VideoTitle>{title}</VideoTitle>
                  <VideoMeta>Genesis Energy · {year}</VideoMeta>
                </VideoCaption>
              </VideoCard>
            </StaggerItem>
          ))}
        </StaggerContainer>
      </SectionInner>
    </CampaignSection>
  </PageWrapper>
);

export default Portfolio;
